import Phaser from 'phaser';
import {
  ComponentType,
} from '@/components/ComponentDefs';
import {
  BehaviorRule,
  ConditionType,
  ActionType,
  TargetModifier,
  getConditionLabel,
  getActionLabel,
  getTargetModifierLabel,
} from '@/behaviors/BehaviorDefs';
import { SoundManager } from '@/systems/SoundManager';
import { showSelectionPopup, createTechSlider } from '@/ui/TechUI';
import { validateAction, getGridComponentTypes, TARGET_RELEVANT_ACTIONS } from '@/ui/BehaviorValidation';
import { t } from '@/i18n/I18n';

const ALL_CONDITIONS = Object.values(ConditionType);
const ALL_ACTIONS = Object.values(ActionType);

const PANEL_BG = 0x0d0d1a;
const PANEL_BORDER = 0x333366;

export interface RulesEditorCallbacks {
  getGridTypes: () => ComponentType[];
}

export class RulesEditorPanel {
  // Rules state
  rules: BehaviorRule[] = [];
  private rulesPanel!: Phaser.GameObjects.Container;
  private rulesContent!: Phaser.GameObjects.Container;
  private ruleDisplays: Phaser.GameObjects.Container[] = [];
  private rulesOpen = false;
  private rulesToggleBtn!: Phaser.GameObjects.Text;
  private rulesScrollOffset = 0;
  private rulesPanelH = 0;
  private rulesPanelY = 0;
  private rulesContentMask: Phaser.Display.Masks.GeometryMask | null = null;

  constructor(
    private scene: Phaser.Scene,
    private rulesX: number,
    private rulesPanelW: number,
    private topSectionH: number,
    private titleH: number,
    private callbacks: RulesEditorCallbacks,
  ) {}

  setup(): void {
    // No EventBus listeners
  }

  destroy(): void {
    for (const d of this.ruleDisplays) d.destroy();
    this.ruleDisplays = [];
  }

  createRulesPanel(): void {
    const toggleX = this.rulesX;
    const toggleY = this.titleH;
    this.rulesToggleBtn = this.scene.add.text(toggleX, toggleY, t('workshopScene.behavior'), {
      fontSize: '11px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
      backgroundColor: '#1a1a3a', padding: { x: 8, y: 5 },
    }).setDepth(10).setInteractive({ useHandCursor: true });

    this.rulesToggleBtn.on('pointerdown', () => {
      SoundManager.play('click');
      this.rulesOpen = !this.rulesOpen;
      this.rulesPanel.setVisible(this.rulesOpen);
      this.rulesToggleBtn.setText(this.rulesOpen ? t('workshopScene.behavior') : t('workshopScene.behaviorHide'));
    });

    this.rulesOpen = true;
    this.rulesPanel = this.scene.add.container(0, 0).setVisible(true).setDepth(10);

    const panelW = this.rulesPanelW;
    const panelH = Math.min(this.topSectionH - 6, 5 * 62 + 40);
    const panelX = this.rulesX;
    const panelY = toggleY + 24;
    this.rulesPanelH = panelH;
    this.rulesPanelY = panelY;

    const bg = this.scene.add.rectangle(panelX + panelW / 2, panelY + panelH / 2, panelW, panelH, 0x0a0a1e, 0.96)
      .setStrokeStyle(1, PANEL_BORDER);
    this.rulesPanel.add(bg);

    const addBtn = this.scene.add.text(panelX + panelW - 70, panelY + 4, t('workshopScene.addRule'), {
      fontSize: '9px', color: '#44cc44', fontFamily: 'monospace', fontStyle: 'bold',
    }).setInteractive({ useHandCursor: true });
    addBtn.on('pointerdown', () => { SoundManager.play('clickConfirm'); this.addRule(); });
    addBtn.on('pointerover', () => addBtn.setColor('#88ff88'));
    addBtn.on('pointerout', () => addBtn.setColor('#44cc44'));
    this.rulesPanel.add(addBtn);

    this.rulesContent = this.scene.add.container(0, 0).setDepth(11);
    this.rulesPanel.add(this.rulesContent);

    const maskGfx = this.scene.make.graphics({ x: 0, y: 0 }, false);
    const maskTop = panelY + 20;
    maskGfx.fillRect(panelX, maskTop, panelW, panelH - 20);
    this.rulesContentMask = maskGfx.createGeometryMask();
    this.rulesContent.setMask(this.rulesContentMask);

    this.rulesScrollOffset = 0;
    bg.setInteractive();
    bg.on('wheel', (_p: Phaser.Input.Pointer, _dx: number, _dy: number, dz: number) => {
      const contentH = this.rules.length * 62;
      const visibleH = this.rulesPanelH - 20;
      const maxScroll = Math.max(0, contentH - visibleH);
      this.rulesScrollOffset = Phaser.Math.Clamp(this.rulesScrollOffset + dz * 0.5, 0, maxScroll);
      this.rulesContent.y = -this.rulesScrollOffset;
    });

    this.refreshRuleDisplays();
  }

  addRule(): void {
    if (this.rules.length >= 5) return;
    this.rules.push({
      priority: this.rules.length + 1,
      condition: ConditionType.ENEMY_DETECTED,
      action: ActionType.SHOOT,
      targetModifier: TargetModifier.CLOSEST,
    });
    this.refreshRuleDisplays();
  }

  removeRule(index: number): void {
    this.rules.splice(index, 1);
    this.rules.forEach((r, i) => r.priority = i + 1);
    this.refreshRuleDisplays();
  }

  swapRulePriority(indexA: number, indexB: number): void {
    [this.rules[indexA], this.rules[indexB]] = [this.rules[indexB], this.rules[indexA]];
    this.rules.forEach((r, i) => r.priority = i + 1);
    this.refreshRuleDisplays();
  }

  refreshRuleDisplays(): void {
    for (const d of this.ruleDisplays) d.destroy();
    this.ruleDisplays = [];

    const baseX = this.rulesX + 6;
    const baseY = this.titleH + 50;

    const contentH = this.rules.length * 62;
    const visibleH = this.rulesPanelH - 20;
    const maxScroll = Math.max(0, contentH - visibleH);
    this.rulesScrollOffset = Math.min(this.rulesScrollOffset, maxScroll);
    this.rulesContent.y = -this.rulesScrollOffset;

    for (let i = 0; i < this.rules.length; i++) {
      const container = this.createRuleRow(this.rules[i], i, baseX, baseY + i * 62);
      this.ruleDisplays.push(container);
      this.rulesContent.add(container);
    }

    if (this.rules.length === 0) {
      const hint = this.scene.add.container(baseX, baseY).setDepth(11);
      hint.add(this.scene.add.text(0, 0, t('workshopScene.noRules'), { fontSize: '9px', color: '#444466', fontFamily: 'monospace' }));
      this.ruleDisplays.push(hint);
      this.rulesContent.add(hint);
    }
  }

  /** Build a single rule row */
  private createRuleRow(rule: BehaviorRule, index: number, baseX: number, y: number): Phaser.GameObjects.Container {
    const container = this.scene.add.container(baseX, y).setDepth(11);
    const gridTypes = this.callbacks.getGridTypes();
    const validation = validateAction(rule.action, gridTypes);
    let cx = 0;

    if (index > 0) {
      const upBtn = this.scene.add.text(cx, 0, '\u25B2', { fontSize: '8px', color: '#666688', fontFamily: 'monospace' })
        .setInteractive({ useHandCursor: true });
      upBtn.on('pointerdown', () => { SoundManager.play('clickCycle'); this.swapRulePriority(index, index - 1); });
      upBtn.on('pointerover', () => upBtn.setColor('#aaaacc'));
      upBtn.on('pointerout', () => upBtn.setColor('#666688'));
      container.add(upBtn);
    }
    if (index < this.rules.length - 1) {
      const downBtn = this.scene.add.text(cx, 12, '\u25BC', { fontSize: '8px', color: '#666688', fontFamily: 'monospace' })
        .setInteractive({ useHandCursor: true });
      downBtn.on('pointerdown', () => { SoundManager.play('clickCycle'); this.swapRulePriority(index, index + 1); });
      downBtn.on('pointerover', () => downBtn.setColor('#aaaacc'));
      downBtn.on('pointerout', () => downBtn.setColor('#666688'));
      container.add(downBtn);
    }
    cx += 14;

    container.add(this.scene.add.text(cx, 4, `${index + 1}.`, { fontSize: '9px', color: '#555577', fontFamily: 'monospace' }));
    cx += 14;

    container.add(this.scene.add.text(cx, 4, t('workshopScene.if'), { fontSize: '9px', color: '#ff8844', fontFamily: 'monospace', fontStyle: 'bold' }));
    cx += 16;

    const condBtn = this.scene.add.text(cx, 0, getConditionLabel(rule.condition), {
      fontSize: '9px', color: '#ffffff', fontFamily: 'monospace',
      backgroundColor: '#222244', padding: { x: 3, y: 4 },
    }).setInteractive({ useHandCursor: true });
    condBtn.on('pointerdown', () => {
      SoundManager.play('click');
      showSelectionPopup<ConditionType>(
        this.scene,
        t('behavior.selectCondition'),
        ALL_CONDITIONS.map(c => ({
          value: c,
          label: getConditionLabel(c),
          description: t(`behavior.conditionDesc.${c}`),
        })),
        (selected) => { rule.condition = selected; this.refreshRuleDisplays(); },
        rule.condition,
      );
    });
    container.add(condBtn);
    cx += 100;

    container.add(this.scene.add.text(cx, 4, '\u2192', { fontSize: '9px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold' }));
    cx += 14;

    const actColor = validation.valid ? '#ffffff' : '#cc8844';
    const actBtn = this.scene.add.text(cx, 0, getActionLabel(rule.action), {
      fontSize: '9px', color: actColor, fontFamily: 'monospace',
      backgroundColor: '#222244', padding: { x: 3, y: 4 },
    }).setInteractive({ useHandCursor: true });
    actBtn.on('pointerdown', () => {
      SoundManager.play('click');
      const currentGridTypes = this.callbacks.getGridTypes();
      showSelectionPopup<ActionType>(
        this.scene,
        t('behavior.selectAction'),
        ALL_ACTIONS.map(a => {
          const v = validateAction(a, currentGridTypes);
          return {
            value: a,
            label: getActionLabel(a),
            description: t(`behavior.actionDesc.${a}`),
            disabled: !v.valid,
            disabledReason: v.missingReason ? t(v.missingReason) : undefined,
          };
        }),
        (selected) => { rule.action = selected; this.refreshRuleDisplays(); },
        rule.action,
      );
    });
    container.add(actBtn);
    cx += 95;

    if (TARGET_RELEVANT_ACTIONS.has(rule.action)) {
      if (!rule.targetModifier) rule.targetModifier = TargetModifier.CLOSEST;
      const modBtn = this.scene.add.text(cx, 0, getTargetModifierLabel(rule.targetModifier), {
        fontSize: '8px', color: '#8888cc', fontFamily: 'monospace',
        backgroundColor: '#1a1a33', padding: { x: 2, y: 4 },
      }).setInteractive({ useHandCursor: true });
      modBtn.on('pointerdown', () => {
        SoundManager.play('clickCycle');
        rule.targetModifier = rule.targetModifier === TargetModifier.CLOSEST
          ? TargetModifier.MOST_ADVANCED : TargetModifier.CLOSEST;
        this.refreshRuleDisplays();
      });
      modBtn.on('pointerover', () => modBtn.setColor('#aaaaee'));
      modBtn.on('pointerout', () => modBtn.setColor('#8888cc'));
      container.add(modBtn);
    }

    if (!validation.valid && validation.missingReason) {
      const warn = this.scene.add.text(this.rulesPanelW - 30, 3, '\u26A0', {
        fontSize: '11px', color: '#cc8844', fontFamily: 'monospace',
      });
      container.add(warn);
    }

    const delBtn = this.scene.add.text(this.rulesPanelW - 16, 3, '\u2715', {
      fontSize: '11px', color: '#cc4444', fontFamily: 'monospace',
    }).setInteractive({ useHandCursor: true });
    delBtn.on('pointerdown', () => { SoundManager.play('clickCancel'); this.removeRule(index); });
    delBtn.on('pointerover', () => delBtn.setColor('#ff6666'));
    delBtn.on('pointerout', () => delBtn.setColor('#cc4444'));
    container.add(delBtn);

    // Row 2: conditional extras
    if (rule.condition === ConditionType.ENEMY_CLOSE) {
      if (!rule.distanceThreshold) rule.distanceThreshold = 200;
      const slider = createTechSlider(
        this.scene, 14, 24,
        `${t('behavior.distance')}: ${rule.distanceThreshold}px`,
        50, 500, 25, rule.distanceThreshold,
        (val) => { rule.distanceThreshold = val; },
        this.rulesPanelW - 50,
      );
      container.add(slider);
    }

    if (!validation.valid && validation.missingReason && rule.condition !== ConditionType.ENEMY_CLOSE) {
      container.add(this.scene.add.text(14, 26, t(validation.missingReason), {
        fontSize: '8px', color: '#cc8844', fontFamily: 'monospace',
      }));
    }

    return container;
  }
}
